import { useMemo } from 'react'
import { useAtom } from 'jotai'
import { MultiPick, TooltipProvider } from '@duck/ui'
import { useAdminTasks } from '@/hooks/useAdminTasks'
import { debugAccessGroupsAtom } from '@/store/debugAtoms'

/**
 * Admin-only: browse the challenges page as a user with the selected access
 * groups. The selection persists and rides along on GET /available-challenges;
 * the backend honors it only for admins. Mounted only while the panel is open,
 * so the admin tasks fetch never runs otherwise. Copy is hardcoded English by
 * panel convention.
 */
export function AccessGroupsTester() {
  const [selected, setSelected] = useAtom(debugAccessGroupsAtom)
  // Groups are derived from tasks (no dedicated endpoint); one large page
  // instead of infinite paging so the list is complete.
  const { tasks, isInitialLoading } = useAdminTasks({ select: ['accessGroups'], initialPageSize: 1000 })

  const options = useMemo(() => {
    const groups = Array.from(new Set(tasks.flatMap((task) => task.accessGroups))).sort()
    // Keep selected groups visible even when no task carries them (typed via allowNewItem).
    for (const group of selected) {
      if (!groups.includes(group)) groups.push(group)
    }
    return groups.map((group) => ({ label: group, value: group }))
  }, [tasks, selected])

  return (
    <div className="flex flex-col gap-2 px-3 py-3">
      <span className="text-sm font-medium">Access groups tester</span>
      {/* Own provider: the panel mounts outside the app's page trees, so the
          clear-search/clear-selections tooltips have no TooltipProvider above. */}
      <TooltipProvider>
        <MultiPick
          options={options}
          selected={selected}
          onChange={setSelected}
          allowNewItem
          loading={isInitialLoading}
          labels={{
            placeholder: 'Browse as groups…',
            searchPlaceholder: 'Search groups…',
            emptyMessage: 'No groups',
            itemsLabel: 'groups',
            createItemLabel: 'Use "{{value}}"',
            clearSelectionsTooltip: 'Clear (back to admin view)',
          }}
        />
      </TooltipProvider>
    </div>
  )
}
